import { Modal, Button } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { useMemo } from "react";

import { useAuth } from "../../hooks/useAuth";
import { fetchActionsUtil } from "../../utils/helpers";
import ConfirmDialog from "../ConfirmDialogue";
import ModalLoader from "../utils/ModalLoader";
import CloseIcon from "mdi-react/CloseIcon";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { PlantStageSections } from "./PlantStageSection";

const insideItem = {
  name: "",
  equipment: "",
  equipmentName: "",
};

const defaultItem = {
  BOM: "",
  BOMName: "",
  stages: [insideItem],
};

const validationSchema = yup.object({
  items: yup.array().of(
    yup.object({
      BOM: yup.string().required("BOM is required"),
      stages: yup.array().of(
        yup.object({
          name: yup.string().required("name is required"),
          equipment: yup.string().required("equipment is required"),
        }),
      ),
    }),
  ),
});

const PlantStagesModal = ({ refetch, onHide, singlePlant, show }) => {
  const { backendUrl } = useAuth();

  const plantId = singlePlant.plantId;

  const fetchSavedStages = async () => {
    const response = await fetchActionsUtil(
      `${backendUrl}/api/production/plant-stages?plant=${plantId}`,
      "GET",
    );

    return response;
  };

  const { data } = useQuery(
    ["PLANT-STAGES", { plantId }],
    () => fetchSavedStages(),
    {
      keepPreviousData: false,
      staleTime: 0,
    },
  );

  const plantStages = useMemo(() => data?.data || [], [data]);

  const { data: equipments } = useQuery(["PLANT_EQUIPMENT", { plantId }], () =>
    fetchActionsUtil(
      `${backendUrl}/api/production/equipments/?plant=${plantId}`,
      "GET",
    ),
  );

  const Equipments = useMemo(() => {
    if (!equipments?.data) return [];
    return equipments?.data?.map((el) => ({
      value: el.Bar_Code,
      label: el.Item_Name,
    }));
  }, [equipments]);

  const fetchBOM = async () => {
    const response = await fetchActionsUtil(
      `${backendUrl}/api/production/output-bills-of-materials?plant=${plantId}`,
      "GET",
    );

    return response;
  };

  const { data: BOMItems, refetch: reFreshBill } = useQuery(
    ["OUTPUT_BILL_OF_QUANTITY", { plantId }],
    () => fetchBOM(plantId),
    {
      keepPreviousData: false,
      staleTime: 0,
    },
  );

  const BOM = useMemo(() => {
    if (!BOMItems) return [];
    return BOMItems?.map((el) => ({
      value: el.Bar_Code ?? String(el?.id),
      label: el.Item_Name ?? el?.templateName,
    }));
  }, [BOMItems]);

  const initialValues = useMemo(() => {
    if (plantStages?.length > 0) {
      return {
        items: [...plantStages],
      };
    }
    return {
      items: [
        {
          ...defaultItem,
        },
      ],
    };
  }, [plantStages]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const newValues = values.items.map((el) => ({ ...el }));
      console.log(newValues);

      if (
        await ConfirmDialog({
          title: "Save Plant stages",
          description: "Are you sure you want to save this stages",
        })
      ) {
        plantStagesMutation.mutate(newValues);
      }
    },
  });

  const plantStagesMutation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/production/plant-stages`,
        "POST",
        "",
        { items: payload, plant: plantId },
      ),
    {
      onSuccess: ({ data, message }) => {
        toast.success(message);

        refetch();
        onHide();
      },
      onError: ({ error }) => {
        toast.error("Unable to save stages");
      },
    },
  );

  // console.log("FORMIK =>", formik.values.items, plantStages);

  return (
    <Modal
      onHide={onHide}
      show={show}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Body>
        <div>
          <div>
            <div className="d-flex justify-content-between">
              <h6>{singlePlant?.plantName.toUpperCase()}</h6>
              <div role="button" className="text-lg" onClick={onHide}>
                <CloseIcon />
              </div>
            </div>
            <p className="text-muted">
              You can add Periodic stages for this plant
            </p>
          </div>
          {/*  */}
          <div className="container mt-3">
            {/*  */}
            <div className="mb-3">
              <FormikProvider value={formik}>
                <form noValidate className="form-relative">
                  {/* */}
                  <FieldArray
                    name="items"
                    render={(arrayHelpers) => (
                      <>
                        {formik.values.items.map((item, index) => (
                          <PlantStageSections
                            key={item.id ?? index}
                            item={item}
                            index={index}
                            formik={formik}
                            arrayHelpers={arrayHelpers}
                            isFirst={index === 0}
                            BOM={BOM}
                            reFreshBill={reFreshBill}
                            insideItem={insideItem}
                            Equipments={Equipments}
                          />
                        ))}

                        <div className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            onClick={() =>
                              arrayHelpers.push({
                                ...defaultItem,
                              })
                            }
                          >
                            Add More
                          </Button>
                        </div>
                      </>
                    )}
                  />
                </form>
              </FormikProvider>
            </div>
          </div>

          {/*  */}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between m-4">
          <button className="btn bg print mx-4" onClick={onHide}>
            Cancel
          </button>
          <Button variant="outline-primary" onClick={formik.handleSubmit}>
            {plantStagesMutation.isLoading
              ? "Sending..."
              : `${plantStages?.length > 0 ? "Update" : "Save"} Plant stages`}
          </Button>
        </div>
      </Modal.Footer>
      <ModalLoader show={plantStagesMutation.isLoading} />
    </Modal>
  );
};

export default PlantStagesModal;
